import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Pencil, Save, X, ArrowUp, ArrowDown, Loader2, Check, AlertCircle } from 'lucide-react';
import FileUploader from './FileUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LANGS = [{ code: 'en', label: 'English' }, { code: 'hi', label: 'हिन्दी' }, { code: 'pa', label: 'ਪੰਜਾਬੀ' }];

const emptyProduct = () => ({
  slug: '', color: 'from-forest-900 to-forest-500',
  name: { en: '', hi: '', pa: '' },
  tagline: { en: '', hi: '', pa: '' },
  description: { en: '', hi: '', pa: '' },
  specs: { en: [''], hi: [''], pa: [''] },
  image_url: '',
});

const Input = ({ testId, value, onChange, placeholder, type = 'input' }) => {
  const Cmp = type === 'textarea' ? 'textarea' : 'input';
  return (
    <Cmp
      data-testid={testId}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={type === 'textarea' ? 3 : undefined}
      className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-800/20"
      style={{ borderColor: '#E5E7EB' }}
    />
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>{label}</label>
    {children}
  </div>
);

export default function ProductsEditor() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // product object being edited
  const [creating, setCreating] = useState(false); // true = creating new
  const [activeLang, setActiveLang] = useState('en');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: '', msg: '' });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/products`);
      setProducts(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openEdit = (p) => { setEditing(JSON.parse(JSON.stringify(p))); setCreating(false); setActiveLang('en'); setStatus({ type: '', msg: '' }); };
  const openCreate = () => { setEditing(emptyProduct()); setCreating(true); setActiveLang('en'); setStatus({ type: '', msg: '' }); };
  const cancelEdit = () => { setEditing(null); setCreating(false); };

  const setLangField = (field, value) => {
    setEditing((p) => ({ ...p, [field]: { ...p[field], [activeLang]: value } }));
  };

  const updateSpecAt = (idx, value) => {
    setEditing((p) => {
      const arr = [...(p.specs[activeLang] || [])];
      arr[idx] = value;
      return { ...p, specs: { ...p.specs, [activeLang]: arr } };
    });
  };
  const addSpec = () => {
    setEditing((p) => ({ ...p, specs: { ...p.specs, [activeLang]: [...(p.specs[activeLang] || []), ''] } }));
  };
  const removeSpec = (idx) => {
    setEditing((p) => ({ ...p, specs: { ...p.specs, [activeLang]: (p.specs[activeLang] || []).filter((_, i) => i !== idx) } }));
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus({ type: '', msg: '' });
    try {
      const payload = { ...editing, specs: {
        en: (editing.specs.en || []).filter(Boolean),
        hi: (editing.specs.hi || []).filter(Boolean),
        pa: (editing.specs.pa || []).filter(Boolean),
      }};
      if (creating) {
        await axios.post(`${BACKEND_URL}/api/admin/products`, payload, { withCredentials: true });
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/products/${editing.id}`, payload, { withCredentials: true });
      }
      await fetchList();
      setStatus({ type: 'success', msg: 'Saved & published live' });
      setTimeout(() => { setEditing(null); setCreating(false); setStatus({ type: '', msg: '' }); }, 900);
    } catch (err) {
      setStatus({ type: 'error', msg: err?.response?.data?.detail || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/products/${id}`, { withCredentials: true });
      await fetchList();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const moveProduct = async (idx, direction) => {
    const newList = [...products];
    const target = idx + direction;
    if (target < 0 || target >= newList.length) return;
    [newList[idx], newList[target]] = [newList[target], newList[idx]];
    setProducts(newList);
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/products/reorder`, { order: newList.map((p) => p.id) }, { withCredentials: true });
    } catch {
      fetchList();
    }
  };

  if (loading) return <div className="py-12 text-center text-sm" style={{ color: '#6B7280' }}><Loader2 className="animate-spin inline mr-2" size={16} /> Loading products...</div>;

  // ── Edit form view ─────────────────────────────────────
  if (editing) {
    const specs = editing.specs?.[activeLang] || [];
    return (
      <div data-testid="products-editor-form" className="max-w-4xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg" style={{ color: '#0A3F22' }}>
            {creating ? 'New Product' : `Edit: ${editing.name?.en || editing.slug}`}
          </h3>
          <button
            data-testid="products-cancel-btn"
            onClick={cancelEdit}
            className="p-2 rounded-lg text-sm hover:opacity-80"
            style={{ backgroundColor: '#F3F4F6', color: '#4A554A' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Shared fields */}
        <div className="bg-white rounded-xl p-6 border space-y-4 mb-4" style={{ borderColor: '#E5E7EB' }}>
          <div className="text-xs font-semibold uppercase tracking-wider mb-2 pb-2 border-b" style={{ color: '#0A3F22', borderColor: '#E5E7EB' }}>Shared</div>
          <Field label="Slug (URL id, e.g. 'rotavator')">
            <Input testId="product-slug" value={editing.slug} onChange={(v) => setEditing((p) => ({ ...p, slug: v }))} placeholder="rotavator" />
          </Field>
          <Field label="Product Image">
            <FileUploader
              testId="product-image-upload"
              currentUrl={editing.image_url}
              onUploaded={(url) => setEditing((p) => ({ ...p, image_url: url }))}
            />
          </Field>
        </div>

        {/* Language tabs */}
        <div className="flex gap-2 mb-3 border-b" style={{ borderColor: '#E5E7EB' }}>
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`product-lang-${l.code}`}
              onClick={() => setActiveLang(l.code)}
              className="px-4 py-2 text-sm font-semibold transition-all border-b-2 -mb-px"
              style={{
                borderColor: activeLang === l.code ? '#D97706' : 'transparent',
                color: activeLang === l.code ? '#0A3F22' : '#9CA3AF',
              }}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl p-6 border space-y-4" style={{ borderColor: '#E5E7EB' }}>
          <Field label="Name">
            <Input testId={`product-name-${activeLang}`} value={editing.name?.[activeLang]} onChange={(v) => setLangField('name', v)} />
          </Field>
          <Field label="Tagline">
            <Input testId={`product-tagline-${activeLang}`} value={editing.tagline?.[activeLang]} onChange={(v) => setLangField('tagline', v)} />
          </Field>
          <Field label="Description">
            <Input testId={`product-description-${activeLang}`} type="textarea" value={editing.description?.[activeLang]} onChange={(v) => setLangField('description', v)} />
          </Field>
          <Field label="Technical Specs (one per row)">
            <div className="space-y-2">
              {specs.map((spec, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    data-testid={`product-spec-${activeLang}-${i}`}
                    value={spec}
                    onChange={(e) => updateSpecAt(i, e.target.value)}
                    placeholder="e.g. Power: 30–60 HP"
                    className="flex-1 px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-800/20"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                  <button
                    type="button"
                    onClick={() => removeSpec(i)}
                    className="p-2 rounded-lg text-sm hover:opacity-80"
                    style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addSpec}
                data-testid={`product-add-spec-${activeLang}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold hover:opacity-80"
                style={{ backgroundColor: '#EAF2EC', color: '#0A3F22' }}
              >
                <Plus size={12} /> Add spec
              </button>
            </div>
          </Field>
        </div>

        <div className="sticky bottom-0 bg-white border-t mt-6 -mx-8 px-8 py-4 flex items-center justify-between" style={{ borderColor: '#E5E7EB' }}>
          <div className="flex items-center gap-2 text-sm">
            {status.type === 'success' && (
              <span data-testid="product-save-success" className="flex items-center gap-1.5" style={{ color: '#059669' }}>
                <Check size={14} /> {status.msg}
              </span>
            )}
            {status.type === 'error' && (
              <span className="flex items-center gap-1.5" style={{ color: '#DC2626' }}>
                <AlertCircle size={14} /> {status.msg}
              </span>
            )}
          </div>
          <button
            data-testid="product-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: '#0A3F22' }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving...' : 'Save & Publish'}
          </button>
        </div>
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────
  return (
    <div data-testid="products-editor" className="max-w-5xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-heading font-bold text-lg" style={{ color: '#0A3F22' }}>Products ({products.length})</h3>
          <p className="text-xs mt-0.5" style={{ color: '#9CA3AF' }}>Add, edit, reorder or delete products. Changes go live instantly.</p>
        </div>
        <button
          data-testid="products-add-btn"
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90"
          style={{ backgroundColor: '#D97706' }}
        >
          <Plus size={14} /> Add Product
        </button>
      </div>

      <div className="space-y-3">
        {products.map((p, idx) => (
          <div key={p.id} data-testid={`product-row-${p.slug || p.id}`} className="bg-white rounded-xl p-4 border flex items-center gap-4" style={{ borderColor: '#E5E7EB' }}>
            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: '#F3F4F6' }}>
              {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-sm" style={{ color: '#0A3F22' }}>{p.name?.en || p.slug}</div>
              <div className="text-xs mt-0.5" style={{ color: '#4A554A' }}>{p.tagline?.en}</div>
              <div className="text-xs mt-1" style={{ color: '#9CA3AF' }}>slug: {p.slug} · {p.specs?.en?.length || 0} specs</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => moveProduct(idx, -1)}
                disabled={idx === 0}
                className="p-2 rounded-lg text-sm hover:opacity-80 disabled:opacity-30"
                style={{ backgroundColor: '#F3F4F6', color: '#4A554A' }}
                aria-label="Move up"
              >
                <ArrowUp size={14} />
              </button>
              <button
                onClick={() => moveProduct(idx, 1)}
                disabled={idx === products.length - 1}
                className="p-2 rounded-lg text-sm hover:opacity-80 disabled:opacity-30"
                style={{ backgroundColor: '#F3F4F6', color: '#4A554A' }}
                aria-label="Move down"
              >
                <ArrowDown size={14} />
              </button>
              <button
                data-testid={`product-edit-${p.slug || p.id}`}
                onClick={() => openEdit(p)}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold hover:opacity-80"
                style={{ backgroundColor: '#EAF2EC', color: '#0A3F22' }}
              >
                <Pencil size={12} /> Edit
              </button>
              <button
                data-testid={`product-delete-${p.slug || p.id}`}
                onClick={() => handleDelete(p.id)}
                className="p-2 rounded-lg text-sm hover:opacity-80"
                style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }}
                aria-label="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
